import { getDefaultMatchingMvfScales } from "@lib/matchingMvf";
import {
  assertGetEvaluator,
  assertGetFunder,
  assertGetRecommender,
  funderUsesMatching,
  getEvaluable,
  getFunderMatchingBudgetRatio,
} from "./accessors";
import type { DbAction, StampableAction } from "./actions";
import type { RoundAggregate } from "./aggregate";
import {
  getInvestigation,
  getProposalDisclosure,
  getReviewProgress,
} from "./proposalMemberData";

export function undoAction(
  action: DbAction | StampableAction,
): ((roundBeforeAction: RoundAggregate) => StampableAction) | null {
  switch (action.type) {
    case "EVALUATION_FEEDBACK_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { memberId, evaluableId } = action;
        const member = roundBeforeAction.members[memberId];
        if (!member) {
          throw new Error("Member not found");
        }
        const { positive, negative } = member.evaluableFeedback[evaluableId]
          ?.positiveNegative ?? { positive: "", negative: "" };
        return {
          type: "EVALUATION_FEEDBACK_CHANGE",
          memberId,
          evaluableId,
          positive,
          negative,
        };
      };
    case "EVALUATION_NOTES_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { memberId, evaluableId } = action;
        const member = roundBeforeAction.members[memberId];
        if (!member) {
          throw new Error("Member not found");
        }
        return {
          type: "EVALUATION_NOTES_CHANGE",
          memberId,
          evaluableId,
          notes: member.evaluableNotes[evaluableId] ?? "",
        };
      };
    case "GENERAL_NOTES_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { memberId } = action;
        const member = roundBeforeAction.members[memberId];
        if (!member) {
          throw new Error("Member not found");
        }
        return {
          type: "GENERAL_NOTES_CHANGE",
          memberId,
          notes: member.generalNotes ?? "",
        };
      };
    case "EVALUATION_SET_HIDDEN":
      return (roundBeforeAction: RoundAggregate) => {
        const { evaluatorId, evaluableId } = action;
        const evaluator = assertGetRecommender(roundBeforeAction, evaluatorId);
        const evaluation = evaluator.evaluations[evaluableId];
        const previousHidden = evaluation?.hidden ?? false;
        return {
          type: "EVALUATION_SET_HIDDEN",
          evaluatorId,
          evaluableId,
          hidden: previousHidden,
        };
      };

    case "EVALUATION_STATE_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { evaluatorId, evaluableId } = action;
        const evaluator = assertGetRecommender(roundBeforeAction, evaluatorId);
        const evaluation = evaluator.evaluations[evaluableId];
        const previousState = evaluation?.state;

        if (!previousState) {
          return {
            type: "EVALUATION_STATE_CHANGE",
            evaluatorId,
            evaluableId,
            change: "cancel",
          };
        }

        switch (previousState) {
          case "canceled":
            return {
              type: "EVALUATION_STATE_CHANGE",
              evaluatorId,
              evaluableId,
              change: "cancel",
            };
          case "deferred":
            return {
              type: "EVALUATION_STATE_CHANGE",
              evaluatorId,
              evaluableId,
              change: "defer",
              deferredTo: evaluation.deferredTo,
            };
          case "recused":
            return {
              type: "EVALUATION_STATE_CHANGE",
              evaluatorId,
              evaluableId,
              change: "recuse",
            };
          case "evaluated":
            return {
              type: "EVALUATION_STATE_CHANGE",
              evaluatorId,
              evaluableId,
              change: "start",
            };
          // not-started, hidden
          default:
            return {
              type: "EVALUATION_STATE_CHANGE",
              evaluatorId,
              evaluableId,
              change: "cancel",
            };
        }
      };

    case "EVALUATION_MVF_CHANGE":
    case "MATCHING_MVF_CHANGE":
    case "EVALUATION_SET_ZERO":
      return (roundBeforeAction: RoundAggregate) => {
        const { evaluatorId, evaluableId } = action;
        const recommenderBeforeAction = assertGetRecommender(
          roundBeforeAction,
          evaluatorId,
        );

        if (!(evaluableId in recommenderBeforeAction.evaluations)) {
          return {
            type: "EVALUATION_STATE_CHANGE",
            evaluatorId,
            evaluableId,
            change: "cancel",
          };
        }

        const previousStandardMvf =
          recommenderBeforeAction.mvfProfile[evaluableId];
        const evaluable = getEvaluable(roundBeforeAction, evaluableId);
        const matchingRate =
          evaluable?.type === "proposal"
            ? evaluable.typedFields.matchRate
            : undefined;

        if (action.type === "MATCHING_MVF_CHANGE") {
          return {
            type: "MATCHING_MVF_CHANGE",
            evaluatorId,
            evaluableId,
            matchingMvfScales:
              recommenderBeforeAction.matchingMvfScales[evaluableId] ??
              getDefaultMatchingMvfScales(matchingRate),
          };
        }

        if (previousStandardMvf == null) {
          return {
            type: "EVALUATION_STATE_CHANGE",
            evaluatorId,
            evaluableId,
            change: "cancel",
          };
        }

        return {
          type: "EVALUATION_MVF_CHANGE",
          evaluatorId,
          evaluableId,
          mvf: previousStandardMvf,
        };
      };

    case "SET_EXPECTED_MATCHING_FRACTION":
      // Legacy action type - no longer used
      return null;

    case "NORMALIZE_EVALUATIONS":
      return (roundBeforeAction: RoundAggregate) => {
        const { evaluatorId } = action;
        const recommenderBeforeAction = assertGetRecommender(
          roundBeforeAction,
          evaluatorId,
        );

        return {
          type: "NORMALIZE_EVALUATIONS",
          evaluatorId,
          restoreMvfProfile: recommenderBeforeAction.mvfProfile,
        };
      };

    case "INVESTIGATION_INTEREST_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { proposalId, memberId } = action;
        const investigation = getInvestigation(
          roundBeforeAction,
          proposalId,
          memberId,
        );
        const interest = investigation?.interest ?? null;
        const confirmedReviewer = investigation?.confirmedReviewer ?? false;

        return {
          type: "INVESTIGATION_INTEREST_CHANGE",
          proposalId,
          memberId,
          interest,
          confirmedReviewer,
        };
      };

    case "REVIEW_PROGRESS_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { proposalId, memberId } = action;
        const reviewProgress = getReviewProgress(
          roundBeforeAction,
          proposalId,
          memberId,
        );
        const progress = reviewProgress ?? null;

        return {
          type: "REVIEW_PROGRESS_CHANGE",
          proposalId,
          memberId,
          progress,
        };
      };

    case "PROPOSAL_DISCLOSURE_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { proposalId, userId } = action;
        const proposalDisclosure = getProposalDisclosure(
          roundBeforeAction,
          proposalId,
          userId,
        );
        const hasDisclosure = proposalDisclosure?.hasDisclosure ?? false;
        const selfRecused = proposalDisclosure?.selfRecused ?? false;
        const disclosure = proposalDisclosure?.disclosure ?? "";

        return {
          type: "PROPOSAL_DISCLOSURE_CHANGE",
          proposalId,
          userId,
          hasDisclosure,
          selfRecused,
          disclosure,
        };
      };

    case "RECUSAL_VOTE":
      return (roundBeforeAction: RoundAggregate) => {
        const { proposalId, votingUserId, subjectUserId } = action;
        const disclosure = getProposalDisclosure(
          roundBeforeAction,
          proposalId,
          subjectUserId,
        );
        const recusalVote = disclosure?.votes[votingUserId] ?? "a";

        return {
          type: "RECUSAL_VOTE",
          proposalId,
          votingUserId,
          subjectUserId,
          vote: recusalVote,
        };
      };

    case "RECOMMENDER_OUTSIDE_DOLLAR_SUBSTITUTION_RATE_CHANGE":
      // Legacy action type - no longer used
      return null;

    case "NOTES_AND_FEEDBACK_VISIBILITY_CHANGE":
    case "ROUND_MEMBER_PROPERTY_CHANGE":
      return null;

    case "EVALUATOR_PROPERTY_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { evaluatorId, property } = action;
        const evaluator = assertGetEvaluator(roundBeforeAction, evaluatorId);
        return {
          type: "EVALUATOR_PROPERTY_CHANGE",
          evaluatorId,
          property,
          value: evaluator.shortname,
        };
      };

    case "FUNDER_BUDGET_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { funderId } = action;
        const funder = assertGetFunder(roundBeforeAction, funderId);
        return {
          type: "FUNDER_BUDGET_CHANGE",
          funderId,
          budget: funder.budget,
        };
      };

    case "FUNDER_SETTLEMENT_BUDGET_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { funderId } = action;
        const funder = assertGetFunder(roundBeforeAction, funderId);
        return {
          type: "FUNDER_SETTLEMENT_BUDGET_CHANGE",
          funderId,
          budget: funder.speculationGrantSettlementBudget ?? 0,
        };
      };

    case "FUNDER_MATCHING_BUDGET_RATIO_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { funderId } = action;
        return {
          type: "FUNDER_MATCHING_BUDGET_RATIO_CHANGE",
          funderId,
          matchingBudgetRatio: getFunderMatchingBudgetRatio(
            roundBeforeAction,
            funderId,
          ),
        };
      };

    case "FUNDER_USES_MATCHING_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { funderId } = action;
        const funder = assertGetFunder(roundBeforeAction, funderId);
        return {
          type: "FUNDER_USES_MATCHING_CHANGE",
          funderId,
          usesMatching: funderUsesMatching(funder),
        };
      };

    case "FUNDER_RATINGS_IMPORT":
      return (roundBeforeAction: RoundAggregate) => {
        const { funderId, scores } = action;
        const funder = assertGetFunder(roundBeforeAction, funderId);
        const prevScores = scores.map(({ recommenderId, proposalId }) => ({
          recommenderId,
          proposalId,
          rating: funder.scores?.[recommenderId]?.[proposalId] ?? undefined,
        }));
        return { type: "FUNDER_RATINGS_IMPORT", funderId, scores: prevScores };
      };

    case "FUNDER_RATING_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { funderId, recommenderId, proposalId } = action;
        const funder = assertGetFunder(roundBeforeAction, funderId);
        const scoreBefore =
          funder.scores?.[recommenderId]?.[proposalId] ?? undefined;
        return {
          type: "FUNDER_RATING_CHANGE",
          funderId,
          recommenderId,
          proposalId,
          rating: scoreBefore,
        };
      };

    // FUNDER_RATINGS_CLEAR wipes every funder's ratings round-wide and is an
    // SFC-admin maintenance action (dispatched with `undoes: null`); it is not
    // part of the client undo/redo stack, so it has no inverse here.

    case "FUNDER_PROPOSAL_WEIGHT_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { funderId, proposalId } = action;
        const funder = assertGetFunder(roundBeforeAction, funderId);
        return {
          type: "FUNDER_PROPOSAL_WEIGHT_CHANGE",
          funderId,
          proposalId,
          weight: funder.proposalWeights[proposalId] ?? null,
        };
      };

    case "FUNDER_PROPOSAL_EXCLUSION_TOGGLE":
      return (roundBeforeAction: RoundAggregate) => {
        const { funderId, proposalId } = action;
        const funder = assertGetFunder(roundBeforeAction, funderId);
        return {
          type: "FUNDER_PROPOSAL_EXCLUSION_TOGGLE",
          funderId,
          proposalId,
          excluded: funder.excludedProposalIds.includes(proposalId),
        };
      };

    case "FUNDER_RECOMMENDER_WEIGHT_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { funderId, recommenderId } = action;
        const funder = assertGetFunder(roundBeforeAction, funderId);
        return {
          type: "FUNDER_RECOMMENDER_WEIGHT_CHANGE",
          funderId,
          recommenderId,
          weight: funder.recommenderWeights[recommenderId] ?? null,
        };
      };

    default:
      return null;
  }
}
